import { View, Text } from "@react-pdf/renderer";
import { stripHtmlToPlainText } from "../../../../../helpers/interventionHtml";
import { PrescriberSuggestionOptions } from "../../../../patient/Patient-Menu/Interventions/data";
import { COLORS, styles } from "../../../../forms/pdf-components/PdfStyles";
import { PdfH2, PdfSubHeading, PdfBody } from "../../../../forms/pdf-components/PdfText";
import {
  PdfRow,
  PdfCol,
  PdfSection,
  PdfDivider,
  PdfVerticalDivider,
} from "../../../../forms/pdf-components/PdfLayout";
import { PdfCheckItem } from "../../../../forms/pdf-components/PdfCheckItem";

/** Underline rule for signature lines */
const SignatureLine = ({ label }) => (
  <View
    style={{
      flexDirection: "row",
      alignItems: "flex-end",
      marginBottom: 4,
    }}
  >
    <Text style={[styles.signatureLabel, { width: 90, flexShrink: 0 }]}>
      {label}
    </Text>
    <View
      style={{
        flex: 1,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        height: 14,
      }}
    />
  </View>
);

/**
 * PdfPrescriberSuggestions
 *
 * Props:
 *   prescriberSuggestions       — string[] of selected suggestion labels
 *   pharmacistRxChangeComments  — HTML/plain text
 *   suggestFollowUpAppointment  — boolean
 *   pharmacistName              — string (pre-computed in InterventionForm)
 */
export const PdfPrescriberSuggestions = ({
  prescriberSuggestions = [],
  pharmacistRxChangeComments = "",
  suggestFollowUpAppointment = false,
  pharmacistName = "",
}) => {
  const selected = Array.isArray(prescriberSuggestions)
    ? prescriberSuggestions
    : prescriberSuggestions
    ? [prescriberSuggestions]
    : [];

  const selectedNormalized = new Set(
    selected.map((v) => (v == null ? "" : v.toString().trim()))
  );

  const prescriberOptions = (PrescriberSuggestionOptions || []).map((opt) => ({
    label: opt?.label,
    checked: selectedNormalized.has(opt?.label),
  }));

  const pharmacistRecommendationOptions = (PrescriberSuggestionOptions || []).map(
    (opt) => ({
      label: opt?.label,
      checked: false,
    })
  );

  const rxChangeCommentsText = stripHtmlToPlainText(
    String(pharmacistRxChangeComments ?? "")
  ).trim();
  const hasRxChangeComments = rxChangeCommentsText.length > 0;

  return (
    <PdfSection style={{ padding: 0 }}>
      {/* ── Header row ── */}
      <PdfRow
        style={{
          borderBottomWidth: 1,
          borderBottomColor: COLORS.border,
          padding: 5,
        }}
      >
        <PdfCol>
          <PdfH2>Pharmacist Suggestions for Prescriber</PdfH2>
        </PdfCol>
        <PdfVerticalDivider />
        <PdfCol>
          <PdfH2>For Prescriber - Please Select One Option</PdfH2>
        </PdfCol>
      </PdfRow>

      {/* ── Checklist row ── */}
      <PdfRow
        style={{
          borderBottomWidth: 1,
          borderBottomColor: COLORS.border,
          padding: 5,
        }}
      >
        <PdfCol>
          {prescriberOptions.map((opt) => (
            <PdfCheckItem
              key={opt.label}
              label={opt.label}
              checked={opt.checked}
            />
          ))}
        </PdfCol>
        <PdfVerticalDivider />
        <PdfCol>
          {pharmacistRecommendationOptions.map((opt) => (
            <PdfCheckItem key={opt.label} label={opt.label} checked={false} />
          ))}
        </PdfCol>
      </PdfRow>

      {/* ── Pharmacist recommendation row ── */}
      <PdfRow
        style={{
          borderBottomWidth: 1,
          borderBottomColor: COLORS.border,
          padding: 5,
        }}
      >
        <PdfCol>
          <PdfSubHeading style={{ marginBottom: 2 }}>
            Pharmacist's Recommendation
          </PdfSubHeading>
          <Text style={styles.bodyText}>
            {hasRxChangeComments
              ? rxChangeCommentsText
              : "No additional recommendations at this time"}
          </Text>
        </PdfCol>
        <PdfVerticalDivider />
        <PdfCol>
          <PdfCheckItem
            label="Suggest to schedule appointment with primary provider"
            checked={suggestFollowUpAppointment}
          />
          {!suggestFollowUpAppointment && (
            <Text style={[styles.fallbackText, { marginLeft: 16 }]}>
              Not applicable at this time
            </Text>
          )}
        </PdfCol>
      </PdfRow>

      {/* ── Rx + Prescriber Review row ── */}
      <PdfRow style={{ minHeight: 240, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
        {/* Rx box */}
        <PdfCol style={{ padding: 8 }}>
          <Text style={styles.rxLarge}>Rx</Text>
        </PdfCol>

        <PdfVerticalDivider />

        {/* Prescriber review */}
        <PdfCol style={{ padding: 8 }}>
          <PdfSubHeading style={{ marginBottom: 4 }}>
            Prescriber Review and Comments
          </PdfSubHeading>
          {/* Empty comment area */}
          <View style={{ flex: 1 }} />
          {/* Signature lines */}
          <SignatureLine label="Prescriber Signature:" />
          <SignatureLine label="Prescriber Name:" />
          <SignatureLine label="Prescriber Licence:" />
        </PdfCol>
      </PdfRow>

      {/* ── Phone auth + Pharmacist Name row ── */}
      <PdfRow
        style={{
          padding: 5,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <View style={{ flex: 1 }}>
          <PdfCheckItem
            label="Select if verbal / phone authorization"
            checked={false}
          />
        </View>

        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-end",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Text style={[styles.signatureLabel, { marginRight: 8 }]}>
            Pharmacist Name:
          </Text>
          <View
            style={{
              flex: 1,
              borderBottomWidth: 1,
              borderBottomColor: COLORS.border,
              minHeight: 14,
              justifyContent: "flex-end",
            }}
          >
            <Text style={styles.pharmacistInfoValue}>{pharmacistName}</Text>
          </View>
        </View>
      </PdfRow>
    </PdfSection>
  );
};
